import {
  AddInventory,
  GetProductsWithPaginationAndSearch,
  GetSingleInventoryDetailById,
} from '@/apis/inventory';
import { GetLocationsWithPaginationAndSearch } from '@/apis/onboarding';
import { LocationData } from '@/apis/types';
import { DeleteIcon, PlusCircleIcon } from '@/assets/icons/icons';
import theme from '@/assets/theme';
import { useToken } from '@/common/helper';
import {
  CustomButton,
  CustomInput,
  CustomSelect,
  CustomTitle,
  SearchInput,
  TeamsSelector,
} from '@/components';
import AddButton from '@/components/AddButton';
import { useSpin } from '@/components/SpinContext';
import { useUnAuthorize } from '@/hooks';
import { Team } from '@/pages/Shipments/CreateShipmentDrawer';
import { themeT } from '@/utils/themeText';
import { DownOutlined } from '@ant-design/icons';
import {
  Col,
  Divider,
  Flex,
  Form,
  Input,
  message,
  Row,
  Spin,
  Switch,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InventoryEditState } from '../InventoryPage';

const { TextArea } = Input;

interface LocationItem {
  location?: string;
  name?: string;
  stock?: string | number;
  serial_numbers?: string;
  reorder_target?: string | number;
  total_required?: string | number;
  is_integration_managed?: boolean;
}

interface ProductTeam {
  id: string;
  name: string;
  color?: string;
}

interface ProductOption {
  id: string;
  name: string;
  product_name?: string | null;
  teams?: ProductTeam[];
  is_active?: boolean;
  [key: string]: unknown;
}

interface SubItem {
  product_id?: string;
  quantity?: number | string;
  name?: string;
}
interface FormValues {
  product_id?: string;
  description?: string;
  teams?: string[];
  sub_items?: SubItem[];
  locations?: LocationItem[];
}

interface RawInventoryResponse {
  id: string;
  name?: string;
  sku?: string;
  product_id?: string;
  product: {
    id: string;
    name: string;
    description: string;
  };
  record_id?: string;
  description: string;
  teams: { id: string; name: string; color: string }[];
  sub_items: {
    id?: string;
    product_id?: string;
    sku?: string;
    name: string;
    product: {
      id: string;
      name: string;
      description: string;
    };
    quantity: number;
    left: number;
    location: { id?: string; label: string; value: number }[];
  }[];
  locations: {
    name: string;
    location_id: string;
    stock: string;
    serial_numbers: string;
    reorder_target: string;
    total_required: string;
    shipped_in: string;
    shipped_out: string;
    stock_state: string | null;
    last_updated_at: string;
    stock_source: string;
    is_integration_managed?: boolean;
  }[];
}

interface InventoryData {
  id?: string | null;
  product_id?: string | null;
  sku?: string;
  description?: string;
  team_ids?: string[];
  sub_items?: {
    product_id?: string;
    quantity?: number;
  }[];
  locations?: {
    facility_id?: string;
    stock?: string | number;
    serial_numbers?: string;
    reorder_target?: string | number;
    total_required?: string | number;
    is_integration_managed?: boolean;
  }[];
}
interface EditInventoryData {
  id?: string | null;
  product_id?: string;
  product?: ProductOption;
  name?: string;
  sku?: string;
  description?: string;
  teams?: string[];
  teamOptions?: ProductTeam[];
  sub_items?: {
    product_id?: string;
    quantity?: number;
  }[];
  locations?: {
    location_id?: string;
    name?: string;
    stock?: number;
    serial_numbers?: string;
    reorder_target?: number;
    total_required?: number;
    is_integration_managed?: boolean;
  }[];
}

interface InventoryFormProps {
  editID?: InventoryEditState;
  drawerOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void | Promise<void>;
  onOpenAddProduct?: () => void;
  productsRefreshKey?: number;
}

const normalizeProductTeams = (teams?: ProductTeam[]): Team[] =>
  (teams ?? []).map((team) => ({
    ...team,
    permissions: {
      is_create: true,
      is_update: true,
    },
  }));

const mergeProductOptions = (
  products: ProductOption[],
  product?: ProductOption | null
): ProductOption[] => {
  if (!product || products.some((item) => item.id === product.id)) {
    return products;
  }

  return [product, ...products];
};

const parseInventoryNumber = (
  input: string | number | undefined,
  fallback?: number
): number | undefined => {
  if (input === undefined || input === '') return fallback;

  const parsed = Number.parseInt(String(input), 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const normalizeSerialNumbers = (input?: string): string =>
  (input ?? '')
    .split(',')
    .map((serial) => serial.trim())
    .filter(Boolean)
    .join(', ');

const InventoryForm = ({
  editID,
  drawerOpen = true,
  onClose,
  onSuccess,
  onOpenAddProduct,
  productsRefreshKey,
}: InventoryFormProps) => {
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const { unauthorize } = useUnAuthorize();

  const [form] = Form.useForm<FormValues>();

  const [value, setValue] = useState('');
  const [isEdit, setIsEdit] = useState(false);

  const [productsList, setProductsList] = useState<ProductOption[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [productDropdownLoading, setProductDropdownLoading] = useState(false);
  const productDropdownScrollElRef = useRef<HTMLDivElement>(null);
  const productDropdownLoadingRef = useRef(false);
  const skipNextProductSearchFetchRef = useRef(false);
  const shouldResetProductDropdownScrollRef = useRef(false);
  const suppressProductScrollLoadUntilRef = useRef(0);
  const lastFetchedProductSearchRef = useRef<string | null>(null);
  const lastProductsRefreshKeyRef = useRef<number | undefined>(
    productsRefreshKey
  );

  const [selectedProductId, setSelectedProductId] = useState<
    string | undefined
  >(undefined);
  const [selectedProduct, setSelectedProduct] = useState<
    ProductOption | undefined
  >(undefined);
  const selectedProductRef = useRef<ProductOption | undefined>(undefined);

  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);

  const [showOptional, setShowOptional] = useState<{ [key: number]: boolean }>(
    {}
  );

  const [locations, setLocations] = useState<LocationItem[]>([
    {
      location: undefined,
      stock: undefined,
      serial_numbers: '',
      reorder_target: undefined,
      total_required: undefined,
      is_integration_managed: false,
    },
  ]);

  const [editData, setEditData] = useState<EditInventoryData | null>(null);

  const [locationsList, setLocationsList] = useState<LocationData[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [dropdownLoading, setDropdownLoading] = useState<boolean>(false);
  const dropdownScrollElRef = useRef<HTMLDivElement>(null);
  const lastFetchedLocationSearchRef = useRef<string | null>(null);
  const [productTotalPages, setProductTotalPages] = useState<number>(1);
  const [productSearchInputKey, setProductSearchInputKey] = useState(0);
  const [productPage, setProductPage] = useState<number>(1);
  const hasInitializedEditFormRef = useRef(false);
  const [subItems, setSubItems] = useState<SubItem[]>([]);
  const [subItemErrors, setSubItemErrors] = useState<{ [key: number]: string }>(
    {}
  );
  const selectedMainProduct = useMemo(() => {
    if (selectedProduct?.id === selectedProductId) return selectedProduct;
    return productsList.find((product) => product.id === selectedProductId);
  }, [productsList, selectedProduct, selectedProductId]);
  const productTeams = useMemo(
    () => normalizeProductTeams(selectedMainProduct?.teams),
    [selectedMainProduct]
  );
  const productOptionsList = useMemo(
    () => mergeProductOptions(productsList, selectedProduct),
    [productsList, selectedProduct]
  );

  useEffect(() => {
    selectedProductRef.current = selectedProduct;
  }, [selectedProduct]);

  const fetchMoreProducts = useCallback(() => {
    if (productDropdownLoadingRef.current || productPage >= productTotalPages) {
      return;
    }

    const nextPage = productPage + 1;
    productDropdownLoadingRef.current = true;
    setProductDropdownLoading(true);

    GetProductsWithPaginationAndSearch(token, nextPage, productSearch)
      .then((response) => {
        const data = response?.data?.data;

        if (data?.is_success) {
          setProductsList((prev) =>
            mergeProductOptions(
              [...prev, ...((data?.data || []) as unknown as ProductOption[])],
              selectedProductRef.current
            )
          );
          setProductPage(nextPage);
        } else {
          message.error(data?.message || 'Failed to load more products');
        }
      })
      .catch((error) => {
        unauthorize(error);
        message.error('Failed to load more products');
      })
      .finally(() => {
        productDropdownLoadingRef.current = false;
        setProductDropdownLoading(false);
      });
  }, [productPage, productTotalPages, token, productSearch, unauthorize]);

  const handleProductPopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (!target) return;

      productDropdownScrollElRef.current = target;

      if (Date.now() < suppressProductScrollLoadUntilRef.current) {
        target.scrollTop = 0;
        return;
      }

      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

      if (nearBottom) fetchMoreProducts();
    },
    [fetchMoreProducts]
  );

  const resetProductDropdownScrollPosition = useCallback(() => {
    if (productDropdownScrollElRef.current) {
      productDropdownScrollElRef.current.scrollTop = 0;
    }

    document
      .querySelectorAll<HTMLElement>(
        '.ant-select-dropdown:not(.ant-select-dropdown-hidden) .rc-virtual-list-holder'
      )
      .forEach((scrollElement) => {
        scrollElement.scrollTop = 0;
      });
  }, []);

  const fetchProducts = useCallback(
    async (searchValue: string) => {
      productDropdownLoadingRef.current = true;
      setProductDropdownLoading(true);

      try {
        const response = await GetProductsWithPaginationAndSearch(
          token,
          1,
          searchValue
        );

        const data = response?.data?.data;

        if (data?.is_success) {
          lastFetchedProductSearchRef.current = searchValue;
          setProductsList(
            mergeProductOptions(
              (data?.data || []) as unknown as ProductOption[],
              selectedProductRef.current
            )
          );
          setProductTotalPages(data?.page_count || 1);
          setProductPage(1);
          return;
        }

        setProductsList([]);
        message.error(data?.message || 'Failed to fetch products');
      } catch (error) {
        unauthorize(error);
        message.error('Failed to fetch products');
        setProductsList([]);
      } finally {
        productDropdownLoadingRef.current = false;
        setProductDropdownLoading(false);
      }
    },
    [token, unauthorize]
  );

  useEffect(() => {
    if (!drawerOpen) return;

    if (skipNextProductSearchFetchRef.current) {
      skipNextProductSearchFetchRef.current = false;
      return;
    }

    if (
      productsList.length > 0 &&
      productSearch === lastFetchedProductSearchRef.current
    ) {
      return;
    }

    const t = setTimeout(() => {
      fetchProducts(productSearch);
    }, 400);

    return () => clearTimeout(t);
  }, [drawerOpen, productSearch, productsList.length, fetchProducts]);

  const refreshProductsFromFirstPage = useCallback(async () => {
    setProductsList([]);
    setProductPage(1);
    setProductTotalPages(1);
    setProductSearchInputKey((prev) => prev + 1);
    shouldResetProductDropdownScrollRef.current = true;
    suppressProductScrollLoadUntilRef.current = Date.now() + 750;
    resetProductDropdownScrollPosition();
    productDropdownScrollElRef.current = null;

    if (productSearch !== '') {
      skipNextProductSearchFetchRef.current = true;
      setProductSearch('');
    }

    await fetchProducts('');
  }, [fetchProducts, productSearch, resetProductDropdownScrollPosition]);

  useEffect(() => {
    if (productsRefreshKey === undefined) return;

    if (lastProductsRefreshKeyRef.current === productsRefreshKey) {
      lastProductsRefreshKeyRef.current = productsRefreshKey;
      return;
    }

    lastProductsRefreshKeyRef.current = productsRefreshKey;
    refreshProductsFromFirstPage();
  }, [productsRefreshKey, refreshProductsFromFirstPage]);

  const fetchInventoryDetails = useCallback(
    async (id?: string) => {
      try {
        showSpin();
        const res = await GetSingleInventoryDetailById(id, token);
        const data = (res?.data?.data?.data as RawInventoryResponse) ?? null;

        if (data) {
          const mappedData: EditInventoryData = {
            id: data.id,
            product_id: data.product.id,
            product: {
              id: data.product.id,
              name: data.product.name,
              teams: data.teams,
              is_active: true,
            },
            name: data.name,
            sku: data.sku,
            description: data.description,
            teams: data.teams.map(
              (team: { id: string; name: string; color: string }) => team.id
            ),
            teamOptions: data.teams,
            sub_items: data.sub_items.map((item) => ({
              product_id: item.product.id,
              quantity: item.quantity,
              name: item.product.name,
            })),
            locations: data.locations.map((loc) => ({
              location_id: loc.location_id,
              name: loc.name,
              stock: parseInventoryNumber(loc.stock, 0),
              serial_numbers: loc.serial_numbers ?? '',
              reorder_target: parseInventoryNumber(loc.reorder_target),
              total_required: parseInventoryNumber(loc.total_required),
            })),
          };

          setEditData(mappedData);
        } else {
          setEditData(null);
        }
      } catch (error) {
        unauthorize(error);
      } finally {
        hideSpin();
      }
    },
    [hideSpin, showSpin, token, unauthorize]
  );
  useEffect(() => {
    hasInitializedEditFormRef.current = false;

    if (editID?.id) {
      setIsEdit(true);
      fetchInventoryDetails(editID?.id);
    } else {
      setIsEdit(false);
      setEditData(null);
    }
  }, [editID?.id, fetchInventoryDetails]);

  const loadLocations = useCallback(
    (pageNum: number, q: string, append = false) => {
      setDropdownLoading(true);
      GetLocationsWithPaginationAndSearch(token, false, pageNum, q)
        .then((res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            lastFetchedLocationSearchRef.current = q;
            const incoming = data?.data?.data || [];
            setLocationsList((prev) =>
              append ? [...prev, ...incoming] : incoming
            );
            setTotalPages(data?.page_count);
            setPage(pageNum);
          } else {
            message.error(data?.message || 'Failed to fetch locations');
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error('Failed to fetch locations');
        })
        .finally(() => setDropdownLoading(false));
    },
    [token, unauthorize]
  );

  useEffect(() => {
    if (!drawerOpen) return;

    if (
      locationsList.length > 0 &&
      search === lastFetchedLocationSearchRef.current
    ) {
      return;
    }

    loadLocations(1, search);
  }, [drawerOpen, search, locationsList.length, loadLocations]);

  const fetchMore = useCallback(() => {
    if (dropdownLoading || page >= totalPages) return;
    loadLocations(page + 1, search, true);
  }, [dropdownLoading, loadLocations, page, search, totalPages]);

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (!target) return;
      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
      if (nearBottom) fetchMore();
    },
    [fetchMore]
  );

  const getDisabledProductIdsForSubItem = (currentIndex: number): string[] => {
    const ids = new Set<string>();

    if (selectedProductId) {
      ids.add(selectedProductId);
    }

    subItems.forEach((item, index) => {
      if (index !== currentIndex && item.product_id) {
        ids.add(item.product_id);
      }
    });

    return Array.from(ids);
  };

  const handleMainProductChange = (productId?: string) => {
    setSelectedProductId(productId);

    const selectedProduct = productsList.find((item) => item.id === productId);
    setSelectedProduct(selectedProduct);
    const allProductTeams = productId
      ? normalizeProductTeams(selectedProduct?.teams)
      : [];

    setSelectedTeams(allProductTeams);

    form.setFieldsValue({
      product_id: productId,
      teams: allProductTeams.map((team) => team.id),
    });

    setSubItemErrors((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        const index = Number(key);
        if (subItems[index]?.product_id === productId) {
          updated[index] = 'Primary product cannot be selected again';
        }
      });
      return updated;
    });
  };
  useEffect(() => {
    if (!selectedProductId) {
      setSelectedProduct(undefined);
      setSelectedTeams([]);
      form.setFieldsValue({ teams: [] });
      return;
    }

    setSelectedTeams(productTeams);
    form.setFieldsValue({
      teams: productTeams.map((team) => team.id),
    });
  }, [form, selectedProductId, productTeams]);

  useEffect(() => {
    if (!editData) return;
    if (hasInitializedEditFormRef.current) return;
    if (!editData.product) return;

    const formValues: FormValues = {
      product_id: editData.product.id,
      description: editData.description,
      teams: editData.teams,
      locations:
        editData.locations?.map((loc) => ({
          location: loc.location_id,
          name: loc.name,
          stock: loc.stock,
          serial_numbers: loc.serial_numbers,
          reorder_target: loc.reorder_target,
          total_required: loc.total_required,
          is_integration_managed: loc.is_integration_managed,
        })) ?? [],
    };

    form.setFieldsValue(formValues);
    setSelectedProductId(editData.product.id);
    setValue(editData.description ?? '');

    if (editData.locations && editData.locations.length > 0) {
      const mappedLocations = editData.locations.map((loc) => ({
        location: loc.location_id,
        stock: loc.stock,
        serial_numbers: loc.serial_numbers,
        reorder_target: loc.reorder_target,
        total_required: loc.total_required,
        is_integration_managed: loc.is_integration_managed,
      }));
      setLocations(mappedLocations);

      const optionalVisibility = editData.locations.reduce<{
        [key: number]: boolean;
      }>((visibility, loc, index) => {
        if (
          loc.serial_numbers ||
          loc.reorder_target !== undefined ||
          loc.total_required !== undefined ||
          loc.is_integration_managed
        ) {
          visibility[index] = true;
        }

        return visibility;
      }, {});

      setShowOptional(optionalVisibility);
    }

    if (editData.sub_items && editData.sub_items.length > 0) {
      setSubItems(editData.sub_items);
    }

    setSelectedProduct(editData.product);

    const editTeams = normalizeProductTeams(editData.teamOptions);

    setSelectedTeams(editTeams);
    form.setFieldsValue({ teams: editTeams.map((team) => team.id) });

    hasInitializedEditFormRef.current = true;
  }, [editData, form]);

  const handleLocationChange = (
    index: number,
    field: string,
    fieldValue: string | number | boolean | undefined
  ) => {
    const updatedLocations = [...locations];
    updatedLocations[index] = {
      ...updatedLocations[index],
      [field]: fieldValue,
    };
    setLocations(updatedLocations);

    const formValue =
      typeof fieldValue === 'number' ? String(fieldValue) : fieldValue;

    form.setFieldsValue({
      locations: updatedLocations.map((loc, i) =>
        i === index ? { ...loc, [field]: formValue } : loc
      ),
    });
  };

  const getSelectedLocationIds = () => {
    return locations.filter((loc) => loc.location).map((loc) => loc.location);
  };

  const addLocation = () => {
    const newLocations = [
      ...locations,
      {
        location: undefined,
        stock: undefined,
        serial_numbers: '',
        reorder_target: undefined,
        total_required: undefined,
        is_integration_managed: false,
      },
    ];

    setLocations(newLocations);
    form.setFieldsValue({ locations: newLocations });
  };

  const removeLocation = (index: number) => {
    if (locations.length <= 1) return;

    const updatedLocations = locations.filter((_, i) => i !== index);
    setLocations(updatedLocations);
    form.setFieldsValue({ locations: updatedLocations });

    const newOptionalVisibility = { ...showOptional };
    delete newOptionalVisibility[index];

    const reindexedVisibility: { [key: number]: boolean } = {};
    Object.keys(newOptionalVisibility).forEach((key) => {
      const numKey = parseInt(key, 10);
      if (numKey > index) {
        reindexedVisibility[numKey - 1] = newOptionalVisibility[numKey];
      } else {
        reindexedVisibility[numKey] = newOptionalVisibility[numKey];
      }
    });

    setShowOptional(reindexedVisibility);
  };

  const toggleOptional = (index: number) => {
    setShowOptional((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleSubItemChange = (
    index: number,
    field: keyof SubItem,
    fieldValue: string | number | undefined
  ) => {
    const updatedSubItems = [...subItems];
    updatedSubItems[index] = {
      ...updatedSubItems[index],
      [field]: fieldValue,
    };

    if (field === 'product_id') {
      const duplicateAgainstMain =
        fieldValue && fieldValue === selectedProductId;
      const duplicateInOtherRows = updatedSubItems.some(
        (item, itemIndex) =>
          itemIndex !== index &&
          item.product_id &&
          item.product_id === fieldValue
      );

      if (duplicateAgainstMain) {
        message.warning('Primary product cannot be selected in sub-items');
        return;
      }

      if (duplicateInOtherRows) {
        message.warning('This product is already selected');
        return;
      }
    }

    setSubItems(updatedSubItems);

    if (field === 'product_id' && fieldValue) {
      setSubItemErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const addSubItem = () => {
    setSubItems((prev) => [...prev, { product_id: undefined, quantity: 1 }]);
  };

  const removeSubItem = (index: number) => {
    const updatedSubItems = subItems.filter((_, i) => i !== index);
    setSubItems(updatedSubItems);

    setSubItemErrors((prev) => {
      const next: { [key: number]: string } = {};
      Object.entries(prev).forEach(([key, val]) => {
        const numericKey = Number(key);
        if (numericKey < index) next[numericKey] = val;
        if (numericKey > index) next[numericKey - 1] = val;
      });
      return next;
    });
  };

  const validateSubItems = () => {
    const errors: { [key: number]: string } = {};

    subItems.forEach((item, index) => {
      if (!item?.product_id && item?.quantity !== undefined) {
        errors[index] = 'Please select a product';
        return;
      }

      if (item?.product_id && item.product_id === selectedProductId) {
        errors[index] = 'Primary product cannot be selected again';
        return;
      }

      const duplicated = subItems.some(
        (subItem, subIndex) =>
          subIndex !== index &&
          subItem.product_id &&
          subItem.product_id === item.product_id
      );

      if (duplicated) {
        errors[index] = 'This product is already selected';
      }
    });

    setSubItemErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetProductDropdownState = useCallback(() => {
    productDropdownScrollElRef.current = null;
  }, []);

  const handleProductDropdownOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        resetProductDropdownState();
        return;
      }

      if (shouldResetProductDropdownScrollRef.current) {
        shouldResetProductDropdownScrollRef.current = false;
        suppressProductScrollLoadUntilRef.current = Date.now() + 1500;
        resetProductDropdownScrollPosition();
        window.setTimeout(resetProductDropdownScrollPosition, 0);
        window.setTimeout(resetProductDropdownScrollPosition, 50);
        window.setTimeout(resetProductDropdownScrollPosition, 150);
      }

      if (!productsList.length && !productDropdownLoadingRef.current) {
        fetchProducts(productSearch);
      }
    },
    [
      fetchProducts,
      productSearch,
      productsList.length,
      resetProductDropdownScrollPosition,
      resetProductDropdownState,
    ]
  );
  const onFinish = async (values: FormValues) => {
    try {
      if (!values?.product_id) {
        message.error('Please select a product');
        return;
      }

      if (!validateSubItems()) {
        return;
      }

      showSpin();

      const primaryProduct =
        selectedMainProduct ||
        productsList.find((product) => product.id === values.product_id);

      const inventoryData: InventoryData = {
        id: editID?.isDuplicate ? null : (editData?.id ?? null),
        product_id: values.product_id,
        sku: primaryProduct?.name || '',
        description: values?.description || '',
        team_ids: selectedTeams.map((team) => team?.id),
        sub_items:
          subItems
            .filter((item) => item?.product_id && Number(item?.quantity) > 0)
            .map((item) => ({
              product_id: item?.product_id,
              quantity: parseInt(String(item?.quantity)) || 0,
            })) || [],
        locations:
          locations.map((location) => {
            const normalizedSerials = normalizeSerialNumbers(
              location?.serial_numbers
            );

            return {
              facility_id: location?.location,
              stock: Number(location?.stock ?? 0),
              reorder_target: location?.reorder_target,
              total_required: location?.total_required,
              is_integration_managed: Boolean(location?.is_integration_managed),
              ...(normalizedSerials
                ? { serial_numbers: normalizedSerials }
                : {}), // ⬅ only added if exists
            };
          }) || [],
      };
      const response = await AddInventory(token, inventoryData);

      if (response?.data?.data?.is_success) {
        message.success(
          response?.data?.data?.message || 'Inventory created successfully'
        );

        form.resetFields();
        setSelectedProductId(undefined);
        setSelectedTeams([]);
        setSubItems([]);
        setLocations([
          {
            location: undefined,
            stock: undefined,
            serial_numbers: '',
            reorder_target: undefined,
            total_required: undefined,
            is_integration_managed: false,
          },
        ]);
        setShowOptional({});
        setValue('');
        setSubItemErrors({});

        await onSuccess?.();
        onClose?.();
      } else {
        message.error(
          response?.data?.data?.message || 'Failed to create inventory'
        );
      }
    } catch (error: unknown) {
      unauthorize(error);
      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error as { data?: { data?: { message?: string } } };
        message.error(errorData?.data?.data?.message || 'An error occurred');
      } else {
        message.error('An error occurred');
      }
    } finally {
      hideSpin();
    }
  };

  return (
    <div>
      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        className='inventory-form-wrapper'
      >
        <div className='drawer-header-1'>
          <CustomTitle
            text={
              editID?.isDuplicate ? 'Duplicate' : isEdit ? 'Edit' : 'Add new'
            }
            fontSize={20}
            fontWeight='700'
            lineHeight='32px'
            color='var(--text-primary)'
            marginBottom={0}
          />
        </div>

        <div className='custom-scrollbar inventory-detail-drawer-wrapper isForm'>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Form.Item
                label='Name'
                className='custom-input form-input-style'
                name='product_id'
                rules={[{ required: true, message: 'Please select product' }]}
              >
                <CustomSelect
                  key={`main-product-select-${productSearchInputKey}`}
                  width='100%'
                  onOpenChange={handleProductDropdownOpenChange}
                  placeholder='Select product'
                  // disabled={isEdit}
                  suffixIcon={
                    <DownOutlined style={{ color: 'var(--text-secondary)' }} />
                  }
                  value={selectedProductId}
                  onChange={(productId) => handleMainProductChange(productId)}
                  options={productOptionsList
                    .filter((item) => item?.is_active !== false)
                    .map((item) => ({
                      value: item.id,
                      label: item.product_name
                        ? `${item.name} (${item.product_name})`
                        : item.name,
                    }))}
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  onPopupScroll={handleProductPopupScroll}
                  height='36px'
                  border='none'
                  placeholderColor='var(--text-secondary)'
                  filterOption={false}
                  notFoundContent={
                    productDropdownLoading ? null : (
                      <div className='p-2'>No products found</div>
                    )
                  }
                  dropdownRender={(menu: React.ReactNode) => (
                    <>
                      <div className='form-dropdown-input'>
                        <SearchInput
                          key={`main-product-search-${productSearchInputKey}`}
                          placeholder='Search product'
                          width='100%'
                          onChange={(val) => setProductSearch(val)}
                          onClear={() => setProductSearch('')}
                        />
                      </div>

                      <div
                        ref={productDropdownScrollElRef}
                        style={{ maxHeight: 256, overflow: 'auto' }}
                        onScroll={(e) => handleProductPopupScroll(e)}
                      >
                        {menu}
                      </div>

                      {productDropdownLoading && (
                        <div
                          style={{
                            padding: 8,
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <Spin size='small' />
                        </div>
                      )}

                      {!isEdit && !editID?.isDuplicate && (
                        <>
                          <Divider style={{ margin: '8px 0' }} />
                          <div
                            className='add-product-btn'
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={() => onOpenAddProduct?.()}
                          >
                            <PlusCircleIcon />
                            <span className='add-product-text'>
                              Add new product
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label='Description'
                name='description'
                rules={[
                  {
                    type: 'string',
                    max: 300,
                    message: 'Max 300 characters allowed',
                  },
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder='Add description here...'
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className='inventory-text-area'
                />
              </Form.Item>
            </Col>

            {selectedProductId && (
              <Col span={24}>
                <Form.Item
                  className='custom-input form-input-style'
                  label={`${themeT('team', theme.themeType)}`}
                  name='teams'
                  rules={[
                    {
                      required: productTeams.length > 0,
                      message: `Please select ${themeT(
                        'team',
                        theme.themeType
                      )?.toLowerCase()}`,
                    },
                  ]}
                >
                  <TeamsSelector
                    hideChevronWhenDisabled
                    teamsOptions={productTeams}
                    selectedTeams={selectedTeams}
                    onChange={() => {}}
                    formState='add'
                    disabled
                  />
                </Form.Item>
              </Col>
            )}

            <Divider className='inventory-divider-style' />

            <Col
              span={24}
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <CustomTitle
                text='Sub-items'
                fontSize={14}
                fontWeight='600'
                lineHeight='16px'
                color='var(--text-primary)'
                marginBottom={0}
              />

              {subItems.map((subItem, index) => (
                <Row gutter={[8, 0]} key={index}>
                  <Col xs={12} md={18}>
                    <div>
                      <CustomSelect
                        key={`subitem-product-select-${index}-${productSearchInputKey}`}
                        onOpenChange={handleProductDropdownOpenChange}
                        width='100%'
                        placeholder='Select product'
                        onPopupScroll={handleProductPopupScroll}
                        suffixIcon={
                          <DownOutlined
                            style={{ color: 'var(--text-secondary)' }}
                          />
                        }
                        value={subItem.product_id}
                        onChange={(productId) =>
                          handleSubItemChange(index, 'product_id', productId)
                        }
                        options={[
                          ...productOptionsList
                            .filter((item) => item?.is_active !== false)
                            .map((item) => ({
                              value: item.id,
                              label: item.product_name
                                ? `${item.name} (${item.product_name})`
                                : item.name,
                              disabled: getDisabledProductIdsForSubItem(
                                index
                              ).includes(item.id),
                            })),

                          ...(editData?.sub_items || [])
                            .filter(
                              (item) =>
                                item.product_id &&
                                !productsList.some(
                                  (product) => product.id === item.product_id
                                )
                            )
                            .map((item) => ({
                              value: item.product_id,
                              label: (item as { name?: string }).name,
                              disabled: getDisabledProductIdsForSubItem(
                                index
                              ).includes(item.product_id as string),
                            })),
                        ]}
                        bgColor='var(--light-gray)'
                        textColor='var(--text-secondary)'
                        borderRadius='8px'
                        height='36px'
                        border='none'
                        placeholderColor='var(--text-secondary)'
                        filterOption={false}
                        notFoundContent={
                          productDropdownLoading ? null : (
                            <div className='p-2'>No products found</div>
                          )
                        }
                        dropdownRender={(menu: React.ReactNode) => (
                          <>
                            <div className='form-dropdown-input'>
                              <SearchInput
                                key={`subitem-product-search-${productSearchInputKey}`}
                                placeholder='Search product'
                                width='100%'
                                onChange={(val) => setProductSearch(val)}
                                onClear={() => setProductSearch('')}
                              />
                            </div>
                            <div
                              ref={productDropdownScrollElRef}
                              style={{ maxHeight: 256, overflow: 'auto' }}
                              onScroll={(e) => handleProductPopupScroll(e)}
                            >
                              {menu}
                            </div>
                            {productDropdownLoading && (
                              <div
                                style={{
                                  padding: 8,
                                  display: 'flex',
                                  justifyContent: 'center',
                                }}
                              >
                                <Spin size='small' />
                              </div>
                            )}
                          </>
                        )}
                      />

                      {subItemErrors[index] && (
                        <div
                          style={{
                            color: 'var(--danger)',
                            fontSize: '11px',
                            marginTop: 4,
                          }}
                        >
                          {subItemErrors[index]}
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <Flex gap={8} align='center'>
                      <CustomInput
                        placeholder='Qty'
                        height='36px'
                        width='60px'
                        border='0px'
                        bgColor='var(--light-gray)'
                        textColor='var(--text-primary)'
                        borderRadius='8px'
                        placeholderColor='var(--text-secondary)'
                        lineHeight='38px'
                        className='quantity-input-padding'
                        value={String(subItem.quantity || '')}
                        onChange={(e) =>
                          handleSubItemChange(index, 'quantity', e.target.value)
                        }
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                      <span
                        onClick={() => removeSubItem(index)}
                        className='h-4 cursor-pointer'
                      >
                        <DeleteIcon />
                      </span>
                    </Flex>
                  </Col>
                </Row>
              ))}

              <div style={{ width: '124px' }}>
                <AddButton
                  text='Add sub-item'
                  addButtonWidth='124px'
                  fontSize='12px'
                  fontWeight='600'
                  onClick={addSubItem}
                />
              </div>
            </Col>

            <Divider className='inventory-divider-style' />

            <Col
              span={24}
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <CustomTitle
                text='Locations'
                fontSize={14}
                fontWeight='600'
                lineHeight='16px'
                color='var(--text-primary)'
                marginBottom={0}
              />

              {locations.map((location, index) => (
                <Flex vertical gap={16} key={index}>
                  <Flex align='center' gap={16}>
                    <Form.Item
                      name={['locations', index, 'location']}
                      rules={[
                        { required: true, message: 'Please select a location' },
                      ]}
                      style={{ flex: 1, margin: 0 }}
                    >
                      <CustomSelect
                        width='100%'
                        placeholder='Select...'
                        suffixIcon={
                          <DownOutlined
                            style={{ color: 'var(--text-secondary)' }}
                          />
                        }
                        value={location.location}
                        onChange={(fieldValue) =>
                          handleLocationChange(index, 'location', fieldValue)
                        }
                        options={[
                          ...locationsList.map((loc) => ({
                            value: loc.id,
                            label: loc.name,
                            disabled: getSelectedLocationIds().includes(loc.id),
                          })),
                          ...(editData?.locations || [])
                            .filter(
                              (loc) =>
                                !locationsList.some(
                                  (apiLoc) => apiLoc.id === loc.location_id
                                )
                            )
                            .map((loc) => ({
                              value: loc.location_id,
                              label: loc.name || 'Unknown Location',
                              disabled: getSelectedLocationIds().includes(
                                loc.location_id
                              ),
                            })),
                        ]}
                        bgColor='var(--light-gray)'
                        textColor='var(--text-secondary)'
                        borderRadius='8px'
                        height='36px'
                        border='none'
                        placeholderColor='var(--text-secondary)'
                        filterOption={false}
                        onPopupScroll={handlePopupScroll}
                        notFoundContent={
                          dropdownLoading ? null : (
                            <div style={{ padding: 8 }}>No locations found</div>
                          )
                        }
                        dropdownRender={(menu: React.ReactNode) => (
                          <>
                            <div className='form-dropdown-input'>
                              <SearchInput
                                placeholder='Search location'
                                width='100%'
                                onChange={(val) => setSearch(val)}
                                onClear={() => setSearch('')}
                              />
                            </div>
                            <div
                              ref={dropdownScrollElRef}
                              style={{ maxHeight: 256, overflow: 'auto' }}
                            >
                              {menu}
                            </div>
                            {dropdownLoading && (
                              <div
                                style={{
                                  padding: 8,
                                  display: 'flex',
                                  justifyContent: 'center',
                                }}
                              >
                                <Spin size='small' />
                              </div>
                            )}
                          </>
                        )}
                      />
                    </Form.Item>

                    {locations.length > 1 && (
                      <div
                        className='cursor-pointer'
                        onClick={() => removeLocation(index)}
                      >
                        <DeleteIcon />
                      </div>
                    )}
                  </Flex>

                  <Row
                    gutter={[0, 8]}
                    style={{
                      paddingLeft: '24px',
                      width: locations.length > 1 ? '91%' : '100%',
                      borderLeft: '1px solid var(--gray-300)',
                    }}
                  >
                    <Col span={24}>
                      <Flex
                        align='center'
                        justify='space-between'
                        className='w-full'
                      >
                        <span className='zl-para-14'>Stock*</span>
                        <Form.Item
                          name={['locations', index, 'stock']}
                          rules={[
                            {
                              required: true,
                              message: 'Please enter value',
                            },
                            {
                              pattern: /^\d{1,10}$/,
                              message:
                                'Stock must be numeric and up to 10 digits',
                            },
                          ]}
                          style={{ margin: 0 }}
                        >
                          <CustomInput
                            placeholder=''
                            value={String(location.stock || '')}
                            onChange={(e) =>
                              handleLocationChange(
                                index,
                                'stock',
                                e.target.value
                              )
                            }
                            bgColor='var(--light-gray)'
                            height='36px'
                            textColor='var(--text-primary)'
                            borderRadius='8px'
                            border='none'
                            placeholderColor='var(--text-secondary)'
                            style={{ width: 120, float: 'right' }}
                            maxLength={10}
                            onKeyPress={(e) => {
                              if (!/[0-9]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Form.Item>
                      </Flex>
                    </Col>

                    {showOptional[index] && (
                      <>
                        <Col span={24}>
                          <Flex
                            align='center'
                            justify='space-between'
                            className='w-full'
                            gap={16}
                          >
                            <span className='zl-para-14'>Serials</span>

                            <CustomInput
                              placeholder=''
                              value={location.serial_numbers || ''}
                              onChange={(e) => {
                                handleLocationChange(
                                  index,
                                  'serial_numbers',
                                  e.target.value
                                );
                              }}
                              onBlur={(e) => {
                                const raw = e.target.value;

                                const normalized = raw
                                  .split(/[\s,]+/) // split on spaces or commas
                                  .map((s) => s.trim()) // trim
                                  .filter(Boolean) // remove empty
                                  .join(','); // join with comma

                                handleLocationChange(
                                  index,
                                  'serial_numbers',
                                  normalized
                                );
                              }}
                              bgColor='var(--light-gray)'
                              height='36px'
                              textColor='var(--text-primary)'
                              borderRadius='8px'
                              border='none'
                              placeholderColor='var(--text-secondary)'
                              style={{ width: 120, float: 'right' }}
                            />
                          </Flex>
                        </Col>

                        <Col span={24}>
                          <Flex
                            align='center'
                            justify='space-between'
                            className='w-full'
                          >
                            <span className='zl-para-14'>Reorder target</span>

                            <CustomInput
                              placeholder=''
                              value={String(location.reorder_target || '')}
                              onChange={(e) =>
                                handleLocationChange(
                                  index,
                                  'reorder_target',
                                  e.target.value
                                )
                              }
                              bgColor='var(--light-gray)'
                              height='36px'
                              textColor='var(--text-primary)'
                              borderRadius='8px'
                              border='none'
                              placeholderColor='var(--text-secondary)'
                              style={{ width: 120, float: 'right' }}
                              maxLength={10}
                              onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </Flex>
                        </Col>

                        <Col span={24}>
                          <Flex
                            align='center'
                            justify='space-between'
                            className='w-full'
                          >
                            <span className='zl-para-14'>
                              Integration managed
                            </span>

                            <Switch
                              checked={Boolean(location.is_integration_managed)}
                              onChange={(checked) =>
                                handleLocationChange(
                                  index,
                                  'is_integration_managed',
                                  checked
                                )
                              }
                            />
                          </Flex>
                        </Col>

                        <Col span={24}>
                          <Flex
                            align='center'
                            justify='space-between'
                            className='w-full'
                          >
                            <span className='zl-para-14'>Total required</span>

                            <CustomInput
                              placeholder=''
                              value={String(location.total_required || '')}
                              onChange={(e) =>
                                handleLocationChange(
                                  index,
                                  'total_required',
                                  e.target.value
                                )
                              }
                              bgColor='var(--light-gray)'
                              height='36px'
                              textColor='var(--text-primary)'
                              borderRadius='8px'
                              border='none'
                              placeholderColor='var(--text-secondary)'
                              style={{ width: 120, float: 'right' }}
                              maxLength={10}
                              onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </Flex>
                        </Col>
                      </>
                    )}

                    <Col span={24}>
                      <span
                        className='underline-link'
                        onClick={() => toggleOptional(index)}
                      >
                        {showOptional[index]
                          ? 'Hide optional fields'
                          : 'Show optional fields'}
                      </span>
                    </Col>
                  </Row>
                </Flex>
              ))}

              <div className='w-124'>
                <AddButton
                  text='Add location'
                  addButtonWidth='124px'
                  fontSize='12px'
                  fontWeight='600'
                  onClick={addLocation}
                />
              </div>
            </Col>
          </Row>

          <div className='drawer-bottom-butotns'>
            <Row gutter={[0, 8]} className='mt-0'>
              <CustomButton
                text={
                  editID?.isDuplicate ? 'Create' : isEdit ? 'Update' : 'Create'
                }
                width='100%'
                height={48}
                borderRadius='8px'
                bgColor='#000'
                border='0px'
                fontSize='16px'
                fontWeight={600}
                htmlType='submit'
              />
              <CustomButton
                key='button1'
                width='100%'
                onClick={() => onClose?.()}
                text='Cancel'
                bgColor='transparent'
                color='black'
                fontSize='16px'
                fontWeight='600'
                boxShadow='none'
                height='48px'
              />
            </Row>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default InventoryForm;
